import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { CreateProductFormSchemaType } from '../common';
import i18n from 'i18n';
import LOCALS from 'commons/locals';
import { FormLabel } from '../form-label';
import { InputNumber } from 'antd';
import { findLabelByValue } from 'commons/options';
import { priceToWithoutTax, priceToWithTax } from 'utils/price-change';
import { STOCK_PLACE_MAP } from 'constant/stock-place';
import { CURRENCY_MAP, CURRENCY_OPTION_LIST } from 'constant/currency';
import { getProductTaxRate } from 'utils/stock-place-currency';

const MAX_PRICE = 99999999;

const ProductPrice = ({
  control,
  errors,
  setValue,
}: {
  control: Control<CreateProductFormSchemaType>;
  errors: FieldErrors<CreateProductFormSchemaType>;
  setValue: UseFormSetValue<CreateProductFormSchemaType>;
}) => {
  const currency = useWatch({ control, name: 'currency' });
  const stockPlace = useWatch({ control, name: 'stockPlace' });
  const taxRate = getProductTaxRate(stockPlace, currency);
  const showPriceWithoutTax =
    currency === CURRENCY_MAP.JPY ||
    stockPlace === STOCK_PLACE_MAP.DUBAI ||
    stockPlace === STOCK_PLACE_MAP.SINGAPORE_ASU_GST ||
    stockPlace === STOCK_PLACE_MAP.FRANCE ||
    stockPlace === STOCK_PLACE_MAP.NEW_YORK;
  const priceWithoutTaxMax = priceToWithoutTax(MAX_PRICE, taxRate);

  return (
    <div className="flex items-center gap-2">
      <FormLabel required>{i18n.t(LOCALS.selling_price)}:</FormLabel>
      <div className="flex w-full gap-2">
        <Controller
          name="price"
          control={control}
          render={({ field }) => (
            <div className="relative w-full">
              <InputNumber
                addonBefore={
                  currency
                    ? findLabelByValue(currency, CURRENCY_OPTION_LIST)
                    : null
                }
                status={errors.price ? 'error' : undefined}
                {...field}
                min={0}
                max={MAX_PRICE}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                // TODO: 消灭 any
                parser={(value) => value!.replace(/\$\s?|(,*)/g, '') as any}
                className="w-full"
                placeholder={i18n.t(LOCALS.please_enter) || ''}
                onChange={(e) => {
                  field.onChange(e);
                  if (e) {
                    setValue('priceWithoutTax', priceToWithoutTax(e, taxRate));
                  }
                }}
              />
              {errors.price && (
                <p style={{ color: 'red' }} className="absolute left-0">
                  {i18n.t(LOCALS.required_field)}
                </p>
              )}
            </div>
          )}
        />

        {showPriceWithoutTax && (
          <div className="flex items-center gap-2 w-full">
            <label className="shrink-0">{i18n.t(LOCALS.czbRYWhFdZ)}:</label>
            <Controller
              name="priceWithoutTax"
              control={control}
              render={({ field }) => (
                <InputNumber
                  className="w-full"
                  {...field}
                  min={0}
                  max={priceWithoutTaxMax}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, '') as any}
                  placeholder={i18n.t(LOCALS.please_enter) || ''}
                  onChange={(e) => {
                    field.onChange(e);
                    if (e) {
                      setValue('price', priceToWithTax(e, taxRate));
                    }
                  }}
                />
              )}
            />
          </div>
        )}

        {stockPlace === STOCK_PLACE_MAP.SHANG_HAI && (
          <Controller
            name="priceHkd"
            control={control}
            render={({ field }) => (
              <InputNumber
                addonBefore="HKD"
                min={0}
                className="w-full"
                {...field}
              />
            )}
          />
        )}
      </div>
    </div>
  );
};

export default ProductPrice;
